import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import {
  DndContext, DragEndEvent, DragOverEvent, DragOverlay, DragStartEvent,
  KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useDroppable,
  useSensor, useSensors,
} from '@dnd-kit/core'
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  COLUMNS, TASK_CATEGORIES,
  deleteTask, genId, getProject, listSheets, listTasksByProject,
  moveTask, saveTask,
} from '../lib/storage'
import type { Column, SavedSheet, TaskCard, TaskCategory } from '../lib/storage'
import BuySheet from './BuySheet'
import StatsBar from './StatsBar'
import { useLocale } from '../i18n/locale'
import { t } from '../i18n/strings'
import type { Locale } from '../i18n/locale'
import { unitLabel } from '../i18n/units'
import { buildShareUrl } from '../lib/shareCodec'

const COL_IDS = COLUMNS.map((c) => c.id)

// Card colour-coding palette. `dot` styles the swatch; `bar` is the left accent.
const CARD_COLORS: Record<string, { dot: string; bar: string; label: string }> = {
  '':     { dot: 'bg-surface-2', bar: '', label: 'None' },
  red:    { dot: 'bg-red-500',    bar: 'border-l-4 border-l-red-500',    label: 'Red' },
  orange: { dot: 'bg-orange-500', bar: 'border-l-4 border-l-orange-500', label: 'Orange' },
  amber:  { dot: 'bg-amber-500',  bar: 'border-l-4 border-l-amber-500',  label: 'Amber' },
  green:  { dot: 'bg-green-500',  bar: 'border-l-4 border-l-green-500',  label: 'Green' },
  teal:   { dot: 'bg-teal-500',   bar: 'border-l-4 border-l-teal-500',   label: 'Teal' },
  blue:   { dot: 'bg-blue-500',   bar: 'border-l-4 border-l-blue-500',   label: 'Blue' },
  purple: { dot: 'bg-purple-500', bar: 'border-l-4 border-l-purple-500', label: 'Purple' },
}
const cardColor = (key: string | undefined) => CARD_COLORS[key ?? ''] ?? CARD_COLORS['']

// Column colour-coding — signals workflow stage at a glance. `top` is the
// thick accent border at the top of each column, `bg` is a subtle tint on the
// header strip, `chip` styles the task-count badge, `dot` is the small dot
// next to the column label. Colours follow standard kanban convention:
//   Planning   → blue   (cool, just starting)
//   Quoted     → purple (researching / gathering info)
//   In Progress → amber (active work in flight)
//   Done       → green  (completed)
// Opacity-based tints (`/10`, `/20`) render correctly in both light and dark
// themes since they paint over whatever surface is behind them.
const COLUMN_COLORS: Record<string, { top: string; bg: string; chip: string; dot: string }> = {
  planning:   { top: 'border-t-4 border-t-blue-500',   bg: 'bg-blue-500/10',   chip: 'bg-blue-500/20 text-blue-700 dark:text-blue-200',     dot: 'bg-blue-500' },
  quoted:     { top: 'border-t-4 border-t-purple-500', bg: 'bg-purple-500/10', chip: 'bg-purple-500/20 text-purple-700 dark:text-purple-200', dot: 'bg-purple-500' },
  inprogress: { top: 'border-t-4 border-t-amber-500',  bg: 'bg-amber-500/10',  chip: 'bg-amber-500/20 text-amber-700 dark:text-amber-200',   dot: 'bg-amber-500' },
  done:       { top: 'border-t-4 border-t-green-500',  bg: 'bg-green-500/10',  chip: 'bg-green-500/20 text-green-700 dark:text-green-200',  dot: 'bg-green-500' },
}
const columnColor = (id: string) => COLUMN_COLORS[id] ?? { top: '', bg: '', chip: 'bg-surface-2 text-text-subtle', dot: 'bg-surface-2' }

interface FormState {
  name: string
  category: TaskCategory
  estimatedCost: string
  note: string
  dueDate: string
}
const BLANK: FormState = { name: '', category: 'other', estimatedCost: '', note: '', dueDate: '' }

// Due-date badge: red if overdue, amber if due within 3 days, muted otherwise.
// Completed tasks just show the date (never "overdue").
function dueBadge(due: string | undefined, isDone: boolean, locale: Locale): { text: string; cls: string } | null {
  if (!due) return null
  const d = new Date(due + 'T00:00:00')
  if (isNaN(d.getTime())) return null
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const dateStr = d.toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-US', { month: 'short', day: 'numeric' })
  const days = Math.round((d.getTime() - today.getTime()) / 86400000)
  const dayUnit = locale === 'fr' ? ' j' : 'd'
  const tr = (k: string) => t(locale, k)
  const amber = 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
  const muted = 'bg-surface-2 text-text-subtle'
  if (isDone) return { text: dateStr, cls: muted }
  if (days < 0) return { text: `${tr('board.overdue')} ${-days}${dayUnit}`, cls: 'bg-danger/15 text-danger' }
  if (days === 0) return { text: tr('board.dueToday'), cls: amber }
  if (days <= 3) return { text: `${tr('board.dueIn')} ${days}${dayUnit}`, cls: amber }
  return { text: dateStr, cls: muted }
}

// Non-interactive card rendered inside <DragOverlay> while a drag is active. The
// real card stays in the source column as a 40%-opacity placeholder; this clone
// is portaled to <body> by dnd-kit so it follows the cursor across columns
// without being clipped by any parent's `overflow-hidden`. Kept intentionally
// minimal — no edit/delete/color/arrow buttons, just the card identity so the
// user knows what they're dragging.
function DragOverlayCard({ task, locale }: { task: TaskCard; locale: Locale }) {
  const tr = (k: string) => t(locale, k)
  const due = dueBadge(task.dueDate, task.column === 'done', locale)
  return (
    <div className={`pointer-events-none rotate-1 rounded-lg border border-border bg-surface p-3 text-sm shadow-2xl ring-2 ring-brand ${cardColor(task.color).bar}`}>
      <div className="font-medium leading-snug text-text">{task.name}</div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        <span className="rounded-full bg-surface-2 px-1.5 py-0.5 text-[10px] capitalize text-text-subtle">
          {tr('cat.' + task.category)}
        </span>
        {task.estimatedCost > 0 && (
          <span className="text-xs font-medium text-brand-text">
            ${task.estimatedCost.toLocaleString()}
          </span>
        )}
        {due && <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${due.cls}`}>{due.text}</span>}
      </div>
      {task.summary && (
        <p className="mt-1.5 line-clamp-2 text-xs text-text-subtle">{task.summary}</p>
      )}
    </div>
  )
}

// Droppable column body — lets cards be dropped into any column (incl. empty ones).
function DroppableColumn({ id, children }: { id: string; children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: `col:${id}` })
  return (
    <div ref={setNodeRef} className={`flex flex-1 flex-col gap-2 p-3 transition-colors ${isOver ? 'bg-brand-tint/40' : ''}`}>
      {children}
    </div>
  )
}

// ─── TaskForm ─────────────────────────────────────────────────────────────────

interface TaskFormProps {
  form: FormState
  onChange: (p: Partial<FormState>) => void
  sheets: SavedSheet[]
  imported: SavedSheet | null
  onImport: (s: SavedSheet) => void
  onSubmit: () => void
  onCancel: () => void
  submitLabel: string
}

function TaskForm({ form, onChange, sheets, imported, onImport, onSubmit, onCancel, submitLabel }: TaskFormProps) {
  const locale = useLocale()
  const tr = (k: string) => t(locale, k)
  const [showImport, setShowImport] = useState(false)
  const f = (p: Partial<FormState>) => onChange(p)

  return (
    <div className="space-y-2">
      <input
        autoFocus
        type="text"
        placeholder={tr('board.taskName')}
        value={form.name}
        onChange={(e) => f({ name: e.target.value })}
        onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
        className="w-full rounded border border-border bg-surface-2 px-2 py-1.5 text-sm text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-brand"
      />
      <div className="grid grid-cols-2 gap-2">
        <select
          value={form.category}
          onChange={(e) => f({ category: e.target.value as TaskCategory })}
          className="rounded border border-border bg-surface-2 px-2 py-1.5 text-sm text-text"
        >
          {TASK_CATEGORIES.map((c) => (
            <option key={c} value={c}>{tr('cat.' + c)}</option>
          ))}
        </select>
        <input
          type="number"
          placeholder={tr('board.cost')}
          value={form.estimatedCost}
          onChange={(e) => f({ estimatedCost: e.target.value })}
          min={0}
          className="rounded border border-border bg-surface-2 px-2 py-1.5 text-sm text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-brand"
        />
      </div>
      <label className="block">
        <span className="mb-0.5 block text-[11px] text-text-subtle">{tr('board.dueDate')}</span>
        <input
          type="date"
          value={form.dueDate}
          onChange={(e) => f({ dueDate: e.target.value })}
          className="w-full rounded border border-border bg-surface-2 px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-1 focus:ring-brand"
        />
      </label>
      <textarea
        placeholder={tr('board.notes')}
        value={form.note}
        onChange={(e) => f({ note: e.target.value })}
        rows={2}
        className="w-full resize-none rounded border border-border bg-surface-2 px-2 py-1.5 text-sm text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-brand"
      />
      {sheets.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowImport((v) => !v)}
            className="text-xs text-brand-text hover:underline"
          >
            {showImport ? tr('board.hideImport') : tr('board.importSheet')}
          </button>
          {showImport && (
            <select
              className="mt-1 w-full rounded border border-border bg-surface-2 px-2 py-1 text-xs text-text"
              defaultValue=""
              onChange={(e) => {
                const s = sheets.find((x) => x.id === e.target.value)
                if (s) onImport(s)
              }}
            >
              <option value="" disabled>{tr('board.select')}</option>
              {sheets.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          )}
          {imported && (
            <p className="mt-0.5 text-xs text-text-subtle">
              {tr('board.using')} <span className="font-medium text-text">{imported.name}</span>
            </p>
          )}
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onSubmit}
          disabled={!form.name.trim()}
          className="flex-1 rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40"
        >
          {submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-border px-3 py-1.5 text-sm text-text-muted hover:bg-surface-2"
        >
          {tr('board.cancel')}
        </button>
      </div>
    </div>
  )
}

// ─── SortableTaskCard ─────────────────────────────────────────────────────────

interface CardProps {
  task: TaskCard
  colIdx: number
  isEditing: boolean
  form: FormState
  onFormChange: (p: Partial<FormState>) => void
  sheets: SavedSheet[]
  imported: SavedSheet | null
  onImport: (s: SavedSheet) => void
  onSave: () => void
  onCancelEdit: () => void
  onEdit: () => void
  onDelete: () => void
  onMove: (dir: 'left' | 'right') => void
  onColor: (color: string) => void
}

function SortableTaskCard({
  task, colIdx, isEditing,
  form, onFormChange, sheets, imported, onImport, onSave, onCancelEdit,
  onEdit, onDelete, onMove, onColor,
}: CardProps) {
  const locale = useLocale()
  const tr = (k: string) => t(locale, k)
  const colorLabel = (key: string) => tr('color.' + (key === '' ? 'none' : key))
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    disabled: isEditing,
  })
  const [colorOpen, setColorOpen] = useState(false)

  const style = { transform: CSS.Transform.toString(transform), transition }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative rounded-lg border border-border bg-surface p-3 text-sm ${cardColor(task.color).bar} ${
        isDragging ? 'opacity-40 shadow-xl ring-1 ring-brand' : ''
      }`}
    >
      {isEditing ? (
        <TaskForm
          form={form}
          onChange={onFormChange}
          sheets={sheets}
          imported={imported}
          onImport={onImport}
          onSubmit={onSave}
          onCancel={onCancelEdit}
          submitLabel={tr('board.save')}
        />
      ) : (
        <div className="flex gap-1.5">
          {/* Drag handle — listeners live HERE (not the whole card) so the
              in-card action buttons keep working. Visible icon is compact, but
              the button's padding gives a generous ~28×36px (desktop) /
              ~36×44px (mobile) hit target. Hover shows the grab cursor +
              background tint so users discover the affordance. */}
          <button
            {...attributes}
            {...listeners}
            tabIndex={-1}
            aria-label={tr('board.dragAria')}
            title={tr('board.dragAria')}
            className="-ml-1 flex shrink-0 cursor-grab touch-none select-none items-center justify-center rounded p-2 text-text-muted hover:bg-surface-2 hover:text-text active:cursor-grabbing sm:p-1.5"
          >
            <svg width="12" height="20" viewBox="0 0 12 20" fill="currentColor" aria-hidden="true">
              <circle cx="3"  cy="3"  r="1.7" />
              <circle cx="3"  cy="10" r="1.7" />
              <circle cx="3"  cy="17" r="1.7" />
              <circle cx="9"  cy="3"  r="1.7" />
              <circle cx="9"  cy="10" r="1.7" />
              <circle cx="9"  cy="17" r="1.7" />
            </svg>
          </button>

          {/* Colour-code swatch (top-right) */}
          <div className="absolute right-2 top-2">
            <button
              type="button"
              onClick={() => setColorOpen((o) => !o)}
              aria-label={tr('board.colorAria')}
              title={tr('board.colorAria')}
              className={`h-4 w-4 rounded-full ring-1 ring-border ${cardColor(task.color).dot}`}
            />
            {colorOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setColorOpen(false)} />
                <div className="absolute right-0 top-6 z-20 flex gap-1 rounded-lg border border-border bg-surface p-1.5 shadow-md">
                  {Object.entries(CARD_COLORS).map(([key, c]) => (
                    <button
                      key={key || 'none'}
                      type="button"
                      onClick={() => { onColor(key); setColorOpen(false) }}
                      aria-label={colorLabel(key)}
                      title={colorLabel(key)}
                      className={`h-5 w-5 rounded-full ${c.dot} ${
                        (task.color ?? '') === key ? 'ring-2 ring-text ring-offset-1 ring-offset-surface' : 'ring-1 ring-border'
                      } ${key === '' ? 'flex items-center justify-center text-[10px] text-text-subtle' : ''}`}
                    >
                      {key === '' ? '✕' : ''}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="min-w-0 flex-1 pr-5">
            <div className="font-medium leading-snug text-text">{task.name}</div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className="rounded-full bg-surface-2 px-1.5 py-0.5 text-[10px] capitalize text-text-subtle">
                {tr('cat.' + task.category)}
              </span>
              {task.estimatedCost > 0 && (
                <span className="text-xs font-medium text-brand-text">
                  ${task.estimatedCost.toLocaleString()}
                </span>
              )}
              {(() => {
                const b = dueBadge(task.dueDate, task.column === 'done', locale)
                return b ? (
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${b.cls}`}>{b.text}</span>
                ) : null
              })()}
            </div>

            {task.lines && task.lines.length > 0 ? (
              /* Materials list, full (no "+N more" truncation). 2-col on
                 narrow card widths, 3-col on wider kanban columns (xl+).
                 unitLabel pluralises the unit per qty + active locale —
                 "1 bag" stays singular, "3 bag" becomes "3 bags", etc.
                 Invariant units (ft, lb, m, kg, …) pass through unchanged. */
              <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-text-muted xl:grid-cols-3">
                {task.lines.map((line, i) => (
                  <li key={i} className="min-w-0 truncate">
                    {line.label}
                    {line.qty != null && (
                      <span className="ml-1 font-medium text-text">{line.qty} {unitLabel(line.unit ?? '', line.qty, locale)}</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : task.summary ? (
              <p className="mt-1.5 line-clamp-2 text-xs text-text-subtle">{task.summary}</p>
            ) : null}

            {task.note && (
              <p className="mt-1.5 line-clamp-1 text-xs italic text-text-subtle">{task.note}</p>
            )}

            <div className="mt-2.5 flex items-center gap-0.5">
              <button
                onClick={() => onMove('left')}
                disabled={colIdx === 0}
                title={colIdx > 0 ? `${tr('board.moveTo')} ${t(locale, 'col.' + COLUMNS[colIdx - 1].id)}` : undefined}
                className="rounded p-1 text-base leading-none text-text-muted hover:bg-surface-2 hover:text-text disabled:opacity-25"
              >←</button>
              <button
                onClick={() => onMove('right')}
                disabled={colIdx === COLUMNS.length - 1}
                title={colIdx < COLUMNS.length - 1 ? `${tr('board.moveTo')} ${t(locale, 'col.' + COLUMNS[colIdx + 1].id)}` : undefined}
                className="rounded p-1 text-base leading-none text-text-muted hover:bg-surface-2 hover:text-text disabled:opacity-25"
              >→</button>
              <button
                onClick={onEdit}
                className="ml-auto rounded px-1.5 py-0.5 text-xs text-text-muted hover:bg-surface-2 hover:text-text"
              >{tr('board.edit')}</button>
              <button
                onClick={onDelete}
                className="rounded px-1.5 py-0.5 text-xs text-danger hover:bg-danger/10"
              >{tr('board.del')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── ProjectBoard ─────────────────────────────────────────────────────────────

export default function ProjectBoard({ projectId }: { projectId: string }) {
  const locale = useLocale()
  const tr = (k: string) => t(locale, k)
  const [tasks, setTasks] = useState<TaskCard[]>([])
  const [loaded, setLoaded] = useState(false)
  const [view, setView] = useState<'board' | 'buysheet'>('board')

  const [addingToCol, setAddingToCol] = useState<Column | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  // ID of the card currently being dragged. Drives the <DragOverlay> clone so
  // the dragged card stays visible while crossing column boundaries.
  const [activeId, setActiveId] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>(BLANK)
  const [imported, setImported] = useState<SavedSheet | null>(null)
  const [savedSheets, setSavedSheets] = useState<SavedSheet[]>([])
  const [sheetsLoaded, setSheetsLoaded] = useState(false)

  const reload = useCallback(async () => {
    // Reload tasks AND saved sheets together. We need saved sheets at render
    // time (not just when opening the task form) because every task with a
    // sheetId resolves its summary/lines live from the saved-sheet store —
    // editing a saved sheet in My Sheets should reflect immediately on every
    // task that uses it. listSheets() is cheap (IndexedDB local), so we
    // re-fetch on every board reload rather than caching across mounts.
    const [t, s] = await Promise.all([listTasksByProject(projectId), listSheets()])
    setTasks(t)
    setSavedSheets(s)
    setSheetsLoaded(true)
    setLoaded(true)
  }, [projectId])

  useEffect(() => { reload() }, [reload])

  const loadSheets = async () => {
    if (sheetsLoaded) return
    setSavedSheets(await listSheets())
    setSheetsLoaded(true)
  }

  // Lookup: sheetId → SavedSheet. Used by `effectiveTasks` below to resolve
  // each task's currently-attached sheet at render time.
  const sheetById = useMemo(() => {
    const m = new Map<string, SavedSheet>()
    for (const s of savedSheets) m.set(s.id, s)
    return m
  }, [savedSheets])

  // Effective view of every task: if a task has a sheetId pointing to an
  // existing saved sheet, swap its summary/lines for the LIVE values from
  // that sheet. If the sheet was deleted (or pre-dates this feature and has
  // no sheetId), fall back to the cached snapshot on the task itself.
  // Use these for DISPLAY only — write paths still use the raw `tasks`.
  const effectiveTasks = useMemo(() => tasks.map((task) => {
    if (!task.sheetId) return task
    const live = sheetById.get(task.sheetId)
    if (!live) return task
    return { ...task, summary: live.summary, lines: live.lines }
  }), [tasks, sheetById])

  // Auto-attach flow: if the user clicked "Attach to project" on a saved sheet
  // in My Sheets, the sheet id was stashed in sessionStorage and we navigated
  // here. On mount, pull the flag, load sheets, find the matching one, and
  // open the Add Task form in the Planning column with the sheet pre-imported.
  // Clears the flag immediately so a manual reload doesn't re-fire the form.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const pendingId = window.sessionStorage.getItem('rs-pending-attach')
    if (!pendingId) return
    window.sessionStorage.removeItem('rs-pending-attach')
    let cancelled = false
    ;(async () => {
      const all = await listSheets()
      if (cancelled) return
      setSavedSheets(all)
      setSheetsLoaded(true)
      const sheet = all.find((s) => s.id === pendingId)
      if (!sheet) return
      setEditingId(null)
      setForm({ ...BLANK, name: sheet.name })
      setImported(sheet)
      setAddingToCol('planning')
    })()
    return () => { cancelled = true }
    // Run once on mount only — the flag is one-shot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const closeForm = () => {
    setAddingToCol(null)
    setEditingId(null)
    setForm(BLANK)
    setImported(null)
  }

  const openAdd = async (col: Column) => {
    await loadSheets()
    setEditingId(null)
    setForm(BLANK)
    setImported(null)
    setAddingToCol(col)
  }

  const openEdit = async (task: TaskCard) => {
    await loadSheets()
    setAddingToCol(null)
    setForm({
      name: task.name,
      category: task.category,
      estimatedCost: task.estimatedCost > 0 ? String(task.estimatedCost) : '',
      note: task.note,
      dueDate: task.dueDate ?? '',
    })
    setImported(null)
    setEditingId(task.id)
  }

  const onImport = (s: SavedSheet) => {
    setImported(s)
    setForm((f) => ({ ...f, name: f.name.trim() || s.name }))
  }

  const submitForm = async () => {
    const name = form.name.trim()
    if (!name) return
    const cost = parseFloat(form.estimatedCost) || 0
    const now = new Date().toISOString()
    // sheetId is the live-sync key. Storing it means future edits to this
    // saved sheet propagate to the task automatically. summary/lines are
    // also written as a cached fallback in case the sheet is later deleted.
    const sheetFields = imported
      ? { sheetId: imported.id, calculatorType: imported.type, summary: imported.summary, lines: imported.lines }
      : {}

    if (editingId) {
      const existing = tasks.find((t) => t.id === editingId)
      if (!existing) return
      await saveTask({ ...existing, name, category: form.category, estimatedCost: cost, note: form.note.trim(), dueDate: form.dueDate || undefined, updatedAt: now, ...sheetFields })
    } else {
      const colCount = tasks.filter((t) => t.column === addingToCol).length
      await saveTask({
        id: genId(), projectId, name,
        category: form.category, column: addingToCol!,
        sheetId: imported?.id,
        calculatorType: imported?.type ?? '',
        summary: imported?.summary ?? '',
        lines: imported?.lines,
        estimatedCost: cost, note: form.note.trim(),
        dueDate: form.dueDate || undefined,
        sortOrder: colCount,
        createdAt: now, updatedAt: now,
      })
    }
    closeForm()
    reload()
  }

  // Build the same bundle used for file export and share-link encoding.
  // Both consumers need a deterministic snapshot of: this project, every task
  // in it, and every saved sheet any task references via sheetId.
  const buildBundle = async () => {
    const project = await getProject(projectId)
    if (!project) return null
    // Pull the freshest data — avoids exporting a stale view if the user just
    // edited something.
    const [allTasks, allSheets] = await Promise.all([listTasksByProject(projectId), listSheets()])
    const refSheetIds = new Set(allTasks.map((t) => t.sheetId).filter((id): id is string => !!id))
    const sheets = allSheets.filter((s) => refSheetIds.has(s.id))
    return {
      app: 'renosheets' as const,
      version: 1,
      exportedAt: new Date().toISOString(),
      projects: [project],
      tasks: allTasks,
      sheets,
    }
  }

  // Per-project export — downloads a JSON file. Recipient uses Import backup
  // on My Sheets (or the dedicated /import page) to load it.
  const exportThisProject = async () => {
    const bundle = await buildBundle()
    if (!bundle) return
    const project = bundle.projects[0]
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const safeName = project.name.replace(/[^\w-]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'project'
    a.download = `renosheets-project-${safeName}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Copy a share link that auto-imports this project when the recipient opens
  // it. URL hash carries the encoded bundle — never sent to the server. For
  // very large projects the URL gets long; we surface a warning so the user
  // knows email clients may truncate the display (the link itself still works).
  const [linkCopiedNote, setLinkCopiedNote] = useState<string | null>(null)
  const copyShareLink = async () => {
    const bundle = await buildBundle()
    if (!bundle) return
    if (typeof window === 'undefined') return
    const url = buildShareUrl(window.location.origin, bundle)
    try {
      await navigator.clipboard.writeText(url)
      const long = url.length > 4000
      setLinkCopiedNote(long ? `${tr('board.linkCopied')} ${tr('board.linkTooLong')}` : tr('board.linkCopied'))
      window.setTimeout(() => setLinkCopiedNote(null), 4500)
    } catch {
      // Clipboard refused — extremely rare. Silently no-op; user can use Export instead.
    }
  }

  const handleMove = async (id: string, dir: 'left' | 'right') => {
    const task = tasks.find((t) => t.id === id)
    if (!task) return
    const i = COL_IDS.indexOf(task.column)
    const next = dir === 'left' ? i - 1 : i + 1
    if (next < 0 || next >= COL_IDS.length) return
    await moveTask(id, COL_IDS[next])
    reload()
  }

  const handleDelete = async (id: string) => {
    if (!confirm(tr('board.deleteConfirm'))) return
    await deleteTask(id)
    setTasks((t) => t.filter((x) => x.id !== id))
  }

  const handleColor = async (id: string, color: string) => {
    const task = tasks.find((t) => t.id === id)
    if (!task) return
    const updated = { ...task, color: color || undefined, updatedAt: new Date().toISOString() }
    await saveTask(updated)
    setTasks((ts) => ts.map((t) => (t.id === id ? updated : t)))
  }

  // ── Drag-and-drop sensors ───────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Snapshot of tasks at drag-start, used to:
  //  - revert state if the drag is canceled (mid-flight column moves get undone)
  //  - know the ORIGINAL column on drag-end so we can re-persist the source col's
  //    sortOrder values after one of its cards left mid-drag.
  const originalTasksRef = useRef<TaskCard[] | null>(null)

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id))
    originalTasksRef.current = tasks
  }

  // Live-shift cards in the DESTINATION column out of the way as the user drags.
  // dnd-kit's per-column SortableContext only animates items it knows about, so
  // we have to MOVE the active task into the destination column in React state
  // mid-drag. Once it's logically a member of that column, the verticalList
  // sorting strategy handles the "make room" animation for free. On drag end
  // we persist; on drag cancel we revert from originalTasksRef.
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    if (!over) return
    const activeId = String(active.id)
    const overId = String(over.id)
    if (activeId === overId) return

    setTasks((current) => {
      const activeTask = current.find((t) => t.id === activeId)
      if (!activeTask) return current

      // Resolve target column from the element under the cursor.
      let destCol: Column
      let overTaskIdx = -1
      if (overId.startsWith('col:')) {
        destCol = overId.slice(4) as Column
      } else {
        const overTask = current.find((t) => t.id === overId)
        if (!overTask) return current
        destCol = overTask.column
        overTaskIdx = current.indexOf(overTask)
      }

      // Same column — sortable strategy handles reorder, nothing for us to do.
      if (activeTask.column === destCol) return current

      // Pull the active task out and re-insert at the hovered position (or the
      // end of the destination column if hovering empty space).
      const without = current.filter((t) => t.id !== activeId)
      const moved = { ...activeTask, column: destCol }
      if (overTaskIdx === -1) {
        // Empty column: append after the LAST task currently in destCol.
        let lastDestIdx = -1
        for (let i = without.length - 1; i >= 0; i--) {
          if (without[i].column === destCol) { lastDestIdx = i; break }
        }
        without.splice(lastDestIdx + 1, 0, moved)
      } else {
        // Insert before the hovered card (its index shifts after the filter).
        const newOverIdx = without.findIndex((t) => t.id === overId)
        without.splice(newOverIdx, 0, moved)
      }
      return without
    })
  }

  const handleDragCancel = () => {
    setActiveId(null)
    if (originalTasksRef.current) setTasks(originalTasksRef.current)
    originalTasksRef.current = null
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null)
    const { active, over } = event
    if (!over) {
      // Released outside any droppable — revert any mid-drag state changes.
      if (originalTasksRef.current) setTasks(originalTasksRef.current)
      originalTasksRef.current = null
      return
    }
    const activeTask = tasks.find((t) => t.id === active.id)
    if (!activeTask) { originalTasksRef.current = null; return }

    // After onDragOver, the active task is already in the column the user is
    // hovering. We just need to finalize the position within that column and
    // persist sortOrder for both the destination and the original source col.
    const overId = String(over.id)
    let overTask: TaskCard | undefined
    if (!overId.startsWith('col:')) {
      overTask = tasks.find((t) => t.id === over.id)
    }
    const destCol = activeTask.column

    // Final reorder within destination column (handles in-column repositioning
    // AND the cross-column case — `tasks` already reflects the in-flight move).
    const colTasks = tasks.filter((t) => t.column === destCol)
    const oldIdx = colTasks.findIndex((t) => t.id === active.id)
    const newIdx = overTask ? colTasks.findIndex((t) => t.id === over.id) : colTasks.length - 1
    const reorderedDest = (oldIdx !== newIdx && oldIdx >= 0 && newIdx >= 0)
      ? arrayMove(colTasks, oldIdx, newIdx)
      : colTasks

    const now = new Date().toISOString()
    const writes: Promise<unknown>[] = reorderedDest.map((t, i) =>
      saveTask({ ...t, column: destCol, sortOrder: i, updatedAt: now }),
    )

    // If the task crossed columns, the source column's sortOrder values need to
    // be re-stamped too (a hole opened up where the active task used to be).
    const originalActive = originalTasksRef.current?.find((t) => t.id === active.id)
    const sourceCol = originalActive?.column
    if (sourceCol && sourceCol !== destCol) {
      const srcTasks = tasks.filter((t) => t.column === sourceCol)
      writes.push(...srcTasks.map((t, i) => saveTask({ ...t, sortOrder: i, updatedAt: now })))
    }

    await Promise.all(writes)
    originalTasksRef.current = null
    reload()
  }

  const formProps = {
    form,
    onFormChange: (p: Partial<FormState>) => setForm((f) => ({ ...f, ...p })),
    sheets: savedSheets,
    imported,
    onImport,
    onSave: submitForm,
    onCancelEdit: closeForm,
  }

  return (
    <div>
      <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex rounded-lg border border-border bg-surface-2 p-0.5">
          {(['board', 'buysheet'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                view === v ? 'bg-surface text-text shadow-sm' : 'text-text-muted hover:text-text'
              }`}
            >
              {v === 'board' ? tr('board.board') : tr('board.buySheet')}
            </button>
          ))}
        </div>
        {/* Share actions: copy a share link (auto-imports on the recipient's
            side via /import#data=…), export to a portable JSON file, or print
            the board for read-only PDF sharing. All three are pure client-side
            — no data leaves the user's device. */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={copyShareLink}
            title={tr('board.copyLinkTitle')}
            className="rounded-md border border-brand bg-brand/5 px-3 py-1.5 text-sm font-medium text-brand-text hover:bg-brand/15"
          >
            {tr('board.copyLink')}
          </button>
          <button
            onClick={exportThisProject}
            title={tr('board.exportProjectTitle')}
            className="rounded-md border border-brand bg-surface px-3 py-1.5 text-sm font-medium text-brand-text hover:bg-brand/10"
          >
            {tr('board.exportProject')}
          </button>
          <button
            onClick={() => window.print()}
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text-muted hover:bg-surface-2"
          >
            {tr('board.printBoard')}
          </button>
        </div>
      </div>
      {linkCopiedNote && (
        <div className="no-print mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
          {linkCopiedNote}
        </div>
      )}

      {loaded && <StatsBar tasks={tasks} />}

      {view === 'buysheet' ? (
        /* BuySheet aggregates line-items across all tasks — use effectiveTasks
           so the aggregated buy list reflects the LIVE attached-sheet data,
           not the cached snapshot stored on each task at import time. */
        <div className="mt-6"><BuySheet tasks={effectiveTasks} /></div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {COLUMNS.map((col) => {
              /* Card rendering pulls summary/lines from effectiveTasks so a
                 sheet edited in My Sheets shows up here without re-importing. */
              const colTasks = effectiveTasks.filter((t) => t.column === col.id)
              const cc = columnColor(col.id)
              return (
                <div key={col.id} className={`flex min-h-[200px] flex-col overflow-hidden rounded-xl border border-border bg-surface-2 ${cc.top}`}>
                  <div className={`border-b border-border px-4 py-3 ${cc.bg}`}>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2 font-semibold text-text">
                        <span className={`h-2 w-2 rounded-full ${cc.dot}`} aria-hidden />
                        {t(locale, 'col.' + col.id)}
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cc.chip}`}>{colTasks.length}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-text-subtle">{t(locale, 'col.' + col.id + '.desc')}</p>
                  </div>

                  <DroppableColumn id={col.id}>
                    <SortableContext items={colTasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                      {colTasks.map((task) => (
                        <SortableTaskCard
                          key={task.id}
                          task={task}
                          colIdx={COL_IDS.indexOf(task.column)}
                          isEditing={editingId === task.id}
                          {...formProps}
                          onEdit={() => openEdit(task)}
                          onDelete={() => handleDelete(task.id)}
                          onMove={(dir) => handleMove(task.id, dir)}
                          onColor={(color) => handleColor(task.id, color)}
                        />
                      ))}
                    </SortableContext>

                    {addingToCol === col.id ? (
                      <div className="rounded-lg border border-border bg-surface p-3">
                        <TaskForm
                          form={form}
                          onChange={(p) => setForm((f) => ({ ...f, ...p }))}
                          sheets={savedSheets}
                          imported={imported}
                          onImport={onImport}
                          onSubmit={submitForm}
                          onCancel={closeForm}
                          submitLabel={tr('board.add')}
                        />
                      </div>
                    ) : (
                      <button
                        onClick={() => openAdd(col.id)}
                        className="mt-auto w-full rounded-lg border border-dashed border-border py-2 text-sm text-text-muted hover:bg-surface hover:text-text"
                      >
                        {tr('board.addTask')}
                      </button>
                    )}
                  </DroppableColumn>
                </div>
              )
            })}
          </div>
          {/* Floating clone of the dragged card. Portaled to <body> by dnd-kit,
              so it stays visible while crossing column boundaries (the columns
              themselves use `overflow-hidden`, which clipped the in-flow drag
              transform — the overlay sidesteps that entirely). */}
          <DragOverlay dropAnimation={null}>
            {activeId ? (() => {
              const dragged = effectiveTasks.find((t) => t.id === activeId)
              return dragged ? <DragOverlayCard task={dragged} locale={locale} /> : null
            })() : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  )
}
